/**
 * ComplianceGatesPage — Compliance · Gating rules ("When this happens, require this").
 *
 * Each row is a check the bank performs before an account can move forward.
 * Full create / edit / soft-delete surface, built to match the ProductsPage
 * editor pattern (reload via config read, DataTable with trailing Edit/Delete
 * ghost buttons, a top "New" Card action, a ModalShell form using FormField
 * primitives, a save() handler, and a window.confirm soft-delete).
 *
 * Reads:  client.retail.config.complianceGates()  (GET /v3/config/compliance-gates)
 * Writes go through the typed bearer-only admin surface (first arg '' = no
 * bypass key; authorizes via the bearer role-claim — no bypass prompt):
 *   client.retail.admin.compliance.complianceGates.upsert('', body)
 *   client.retail.admin.compliance.complianceGates.remove('', gateCode)  // soft delete
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComplianceGateUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

/**
 * Wire row as returned by GET /v3/config/compliance-gates. The config read is
 * typed loosely (returns `any`), so we define the row shape locally and cast.
 */
interface GateRow {
  gateCode: string;
  displayName: string;
  description: string | null;
  category: string | null;
  isHardGate: boolean;
  regulatoryBasis: string | null;
  coreFields: string | null;
  coreAction: string | null;
  sortOrder: number | null;
  isActive: boolean;
}

// Category ENUM — copied verbatim from the legacy ROP-teams editor
// (apps/ROP-teams/src/modules/Admin/editors/complianceGatesEditor.tsx CATEGORIES).
// Do NOT invent category values.
const CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Identity', label: 'Identity — verify who they are' },
  { value: 'Sanctions', label: 'Sanctions — OFAC and watchlists' },
  { value: 'KYC', label: 'KYC — know your customer' },
  { value: 'Tax', label: 'Tax — TIN, W-9, withholding' },
  { value: 'BSA', label: 'BSA — Bank Secrecy Act' },
  { value: 'Custom', label: 'Custom — bank-specific rule' },
];

interface EditDraft {
  isNew: boolean;
  gateCode: string;
  displayName: string;
  description: string;
  category: string;
  isHardGate: boolean;
  regulatoryBasis: string;
  coreFields: string;
  coreAction: string;
  sortOrder: number | null;
  isActive: boolean;
}

function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function rowToDraft(r: GateRow): EditDraft {
  return {
    isNew: false,
    gateCode: r.gateCode,
    displayName: r.displayName,
    description: r.description ?? '',
    category: r.category ?? 'Identity',
    isHardGate: r.isHardGate ?? true,
    regulatoryBasis: r.regulatoryBasis ?? '',
    coreFields: r.coreFields ?? '',
    coreAction: r.coreAction ?? '',
    sortOrder: toNum(r.sortOrder),
    isActive: r.isActive,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    gateCode: '',
    displayName: '',
    description: '',
    category: 'Identity',
    isHardGate: true,
    regulatoryBasis: '',
    coreFields: '',
    coreAction: '',
    sortOrder: 100,
    isActive: true,
  };
}

function draftToBody(d: EditDraft): ComplianceGateUpsertBody {
  const trim = (s: string): string => s.trim();
  const nullable = (s: string): string | null => {
    const t = s.trim();
    return t === '' ? null : t;
  };
  return {
    gateCode: trim(d.gateCode),
    displayName: trim(d.displayName),
    description: nullable(d.description),
    category: nullable(d.category),
    isHardGate: d.isHardGate,
    regulatoryBasis: nullable(d.regulatoryBasis),
    coreFields: nullable(d.coreFields),
    coreAction: nullable(d.coreAction),
    sortOrder: d.sortOrder,
    isActive: d.isActive,
  };
}

export function ComplianceGatesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<GateRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = (await client.retail.config.complianceGates()) as {
        success?: boolean;
        data?: ReadonlyArray<GateRow>;
        error?: string;
      };
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data);
      } else {
        setError(resp.error ?? 'Failed to load compliance gates.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter((r) =>
      r.gateCode.toLowerCase().includes(n) ||
      r.displayName.toLowerCase().includes(n) ||
      (r.category ?? '').toLowerCase().includes(n) ||
      (r.regulatoryBasis ?? '').toLowerCase().includes(n)
    );
  }, [rows, filter]);

  const remove = useCallback(
    async (gateCode: string): Promise<void> => {
      if (!window.confirm(`Deactivate gate ${gateCode}? It will be soft-deleted (isActive=false).`)) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.compliance.complianceGates.remove('', gateCode);
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<GateRow>> = useMemo(() => [
    {
      header: 'Code',
      cell: (r) => <code className="cell-mono--bold">{r.gateCode}</code>,
    },
    { header: 'Friendly Name', cell: (r) => r.displayName },
    {
      header: 'Category',
      cell: (r) =>
        r.category ? <Pill tone="muted">{r.category}</Pill> : <span className="cell-dim">—</span>,
    },
    {
      header: 'On Fail',
      cell: (r) =>
        r.isHardGate ? <Pill tone="err">hard stop</Pill> : <Pill tone="warn">warning</Pill>,
    },
    {
      header: 'Reg. Basis',
      cell: (r) =>
        r.regulatoryBasis ? (
          <span className="cell-mono">{r.regulatoryBasis}</span>
        ) : (
          <span className="cell-dim">—</span>
        ),
    },
    {
      header: 'Status',
      cell: (r) =>
        r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
    },
    {
      header: '',
      cell: (r) => (
        <div className="cell-actions">
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void remove(r.gateCode)}>
            Delete
          </Button>
        </div>
      ),
    },
  ], [remove]);

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.compliance.complianceGates.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) return <Banner tone="err">Could not load compliance gates: {error}</Banner>;
  if (!rows) return <Spinner label="Loading compliance gates…" />;

  return (
    <>
      <Card
        title={`Compliance Gates — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by code, name, category, or regulatory basis…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <DataTable<GateRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.gateCode}
          emptyState={<p className="empty">No compliance gates match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft ? (draft.isNew ? 'New compliance gate' : `Edit gate — ${draft.gateCode}`) : ''}
        onClose={() => (saving ? undefined : setDraft(null))}
        width={620}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Gate Code"
              value={draft.gateCode}
              onChange={(v) => setDraft({ ...draft, gateCode: v })}
              disabled={!draft.isNew}
              monospace
              required
              hint={draft.isNew ? 'Short identifier — e.g. CIP, OFAC, CDD. Permanent.' : 'Read-only after create.'}
            />
            <TextField
              label="Friendly Name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v })}
              required
              hint="Plain-English name shown in the UI."
            />
            <TextArea
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={3}
              hint="Why this gate exists. One or two sentences."
            />
            <SelectField
              label="Category"
              value={draft.category}
              onChange={(v) => setDraft({ ...draft, category: v })}
              options={CATEGORY_OPTIONS}
            />
            <BoolField
              label="Hard Gate"
              value={draft.isHardGate}
              onChange={(v) => setDraft({ ...draft, isHardGate: v })}
              hint="On = hard stop (block until cleared). Off = warning (flag and continue)."
            />
            <TextField
              label="Regulatory Basis"
              value={draft.regulatoryBasis}
              onChange={(v) => setDraft({ ...draft, regulatoryBasis: v })}
              hint="Cite the rule or section. e.g. FinCEN CDD Rule 31 CFR 1010.230."
            />
            <TextField
              label="Core Director Fields"
              value={draft.coreFields}
              onChange={(v) => setDraft({ ...draft, coreFields: v })}
              monospace
              hint="Which Core Director fields this gate reads or writes (comma-separated). e.g. 254, 256-258."
            />
            <TextField
              label="jXchange Action"
              value={draft.coreAction}
              onChange={(v) => setDraft({ ...draft, coreAction: v })}
              monospace
              hint="The Core Director op that runs this gate. Blank = humans only. e.g. IdVerifyAdd, OFACSrch."
            />
            <NumberField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
